import { useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Loader2 } from 'lucide-react';
import { LandingPage } from '@/pages/landing';
import { Button } from '@/components/ui/button';
import { useSession } from '@/contexts/session-context';
import { useWorkspace } from '@/contexts/workspace-context';
import { useTrackReferralSignup } from '@/hooks/use-track-referral-signup';
import { peekHandoffRedirectPending } from '@/lib/handoff-storage';

/**
 * Phase 118 — root path `/` decision tree.
 *
 * CONTEXT.md §Root-redirect decision tree:
 *   1. Unauthed + done loading → render LandingPage
 *   2. Unauthed but still loading auth → render Loader spinner
 *   3. Authed + WorkspaceProvider loading → render Loader spinner
 *   4. Authed + activeWorkspace present → Navigate to /w/:activeWs
 *   5. Authed + workspaces present → Navigate to /w/:firstTeamWs (prefer team, fall back to first)
 *   5b. Authed + zero workspaces in the SESSION org, BUT the cross-org union
 *      shows the user has workspaces in another org → the session is scoped to
 *      an empty org (e.g. the signup "home" org after its workspaces moved/were
 *      removed, while the user remains admin of orgs that DO have workspaces).
 *      Re-scope into the first such org via switchOrganization rather than
 *      dead-ending at case 6 — this route is chrome-less, so the org switcher
 *      is otherwise unreachable. A multi-org user must never see "contact
 *      support" while real workspaces exist for them elsewhere (SEV1).
 *   6. Authed + zero workspaces ANYWHERE (empty cross-org union). Two distinct
 *      sub-cases under the SaaS flat model (spec 2026-06-09 Decision 4):
 *      6a. The user belongs to an organization but is an org `member` who has
 *          not been assigned to any workspace yet (a freshly-accepted Team
 *          invite). Per Decision 4 an org member sees "only assigned
 *          workspaces; no Company/Team/Billing", so with zero assignments they
 *          have no accessible surface until an org admin assigns them. This is
 *          a normal, expected state — NOT an error — so it gets actionable
 *          guidance ("ask an org admin"), not a "contact support" dead-end.
 *      6b. No organization context at all → genuine defensive fallback.
 *      Both render a Sign Out affordance so the user is never trapped on a
 *      chrome-less page (no nav, no org switcher reach this route).
 */
export function RootRedirect() {
  const {
    isLoading: authLoading,
    user,
    organizationId,
    role,
    signOut,
    switchOrganization,
  } = useSession();

  // Affiliate Program Task 9 — bind the FP attribution cookie to the WorkOS
  // user the moment they land on `/` after signup (or login). Shared with the
  // standalone invite flow, which never mounts this component.
  useTrackReferralSignup(user);
  const { workspaces, activeWorkspace, organizations, loading: wsLoading } = useWorkspace();

  // AIT-529 Task 7 -- preserve query params (e.g. ?connect=1 from
  // InviteCompletePage) across the workspace-landing redirect so
  // DashboardLayout can act on them once mounted.
  const location = useLocation();

  // Case 5b recovery: the exposed `workspaces` is the SESSION-org-scoped subset,
  // while `organizations` is derived from the full cross-org union. When the
  // session org has zero workspaces but the union shows another org that does,
  // re-scope the session into it instead of falling through to the case-6
  // dead-end. `organizations` only ever lists orgs that HAVE workspaces (it is
  // built from the workspace union), so any entry whose org publicId differs
  // from the current session org is a valid recovery target.
  const recoveryOrg = useMemo(
    () =>
      !wsLoading && user && workspaces.length === 0
        ? organizations.find((o) => o.publicId !== organizationId) ?? null
        : null,
    [wsLoading, user, workspaces.length, organizations, organizationId],
  );
  // Fire the org switch exactly once per recovery target — switchOrganization
  // flips the session organizationId, which re-derives the org-scoped subset
  // from the already-loaded union (no refetch) and lands case 4/5 on the next
  // render.
  const recoveringRef = useRef<string | null>(null);
  useEffect(() => {
    if (!recoveryOrg) return;
    if (recoveringRef.current === recoveryOrg.publicId) return;
    recoveringRef.current = recoveryOrg.publicId;
    // switchOrganization expects the org_<8> publicId — the same id shape the
    // session organizationId carries (AIT-182).
    void switchOrganization(recoveryOrg.publicId);
  }, [recoveryOrg, switchOrganization]);

  // Case 2: auth still resolving and we have no user yet
  if (authLoading && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Case 1: unauth → landing
  if (!user) return <LandingPage />;

  // Case 3: authed but workspaces still fetching
  if (wsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Demo-handoff landing: when this tab captured a handoff JWT (the user came
  // from the marketing demo and just signed in), the first landing is Quick
  // Start — that's the "get started" the demo promised. QuickStartPage clears
  // the flag on mount, so ordinary logins afterwards land normally.
  const workspaceLanding = (wsId: string) => {
    const base = peekHandoffRedirectPending() ? `/w/${wsId}/sandbox/quick-start` : `/w/${wsId}`;
    return `${base}${location.search}`;
  };

  // Case 4: active workspace → go there. A customer is a drill-in, never a
  // default landing — if a stale/legacy active id points at one, fall through
  // to Case 5 (prefer team) so root never strands you inside a client.
  if (activeWorkspace && activeWorkspace.kind !== 'customer') {
    return <Navigate to={workspaceLanding(activeWorkspace.id)} replace />;
  }

  // Case 5: no active workspace -> prefer a TEAM workspace as the default
  // landing (don't drop a user into a customer scope by default); fall back to
  // any workspace so customer-only users still land somewhere.
  if (workspaces.length > 0) {
    const landing = workspaces.find((w) => w.kind === 'team') ?? workspaces[0];
    return <Navigate to={workspaceLanding(landing.id)} replace />;
  }

  // Case 5b: re-scoping into an org that has workspaces — show the spinner
  // while switchOrganization resolves (the effect above fires it). Next render
  // lands case 4/5.
  if (recoveryOrg) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Case 6a: org member with no workspace assignment yet (accepted Team invite,
  // awaiting assignment). Expected state, actionable guidance.
  //
  // Fix round 1 (Ruling 4): a freshly-signed-up invited client can land here
  // (or in 6b) with ?connect=1 still on the URL if their workspace hasn't
  // provisioned yet. A plain `<Link to="/">` wouldn't refetch (same pathname,
  // no remount) -- window.location.reload() re-requests this exact URL,
  // which natively carries the query string, so the eventual workspaceLanding()
  // Navigate still picks up ?connect=1 once workspaces resolve.
  if (organizationId && role !== 'admin') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-lg font-medium">You're all set up, almost.</p>
        <p className="max-w-md text-base text-muted-foreground">
          You've been added to the organization but haven't been assigned to a
          workspace yet. Ask an organization admin to assign you to one, then
          refresh this page.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <Button variant="outline" onClick={() => void signOut()}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // Case 6b: no organization context at all → genuine defensive fallback.
  // Also reachable by a freshly-signed-up invited client (org admin, zero
  // workspaces yet) -- same Refresh rationale as 6a.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="max-w-md text-base text-muted-foreground">
        No workspaces available. Please contact support.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Refresh
        </Button>
        <Button variant="outline" onClick={() => void signOut()}>
          Sign Out
        </Button>
      </div>
    </div>
  );
}
